import { unparse } from 'papaparse';
import type { UnparseConfig, UnparseObject } from 'papaparse';

import { AccountGroupMembership } from '@libs/src/models/account.model';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { EVENT_SUBMISSION_SELECTED_STATUS } from '@main-client/src/app/events/event-submission-selected-status.enum';
import { OPPORTUNITY_TYPES } from '@main-client/src/app/opportunity/opportunity-types.enum';
import {
  Opportunity,
  OpportunitySubmission,
} from '@main-client/src/app/opportunity/opportunity.model';

import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';

export interface UploadCSVAccount {
  _id?: string;
  email?: string;
  errors?: string[];
  groups?: AccountGroupMembership[];
}

export interface UploadCSVUniqueColumn {
  columnName: string;
  columnValues: string[];
}

export interface UploadCSVResponse {
  accounts: UploadCSVAccount[];
  unique_columns: UploadCSVUniqueColumn[];
}

const csvUserDocumentHeadersForRsvpExport = [
  {
    label: 'First Name',
    document_key: 'first_name',
  },
  {
    label: 'Last Name',
    document_key: 'last_name',
  },
  {
    label: 'Email',
    document_key: 'email',
  },
  {
    label: 'Role',
    document_key: 'groups.role',
  },
  {
    label: 'Job Title',
    document_key: 'experience[0].job_title',
  },
  {
    label: 'Organization Name',
    document_key: 'experience[0].organization_name',
  },
];
const csvExclusiveEventSelectedStatusHeaderForRsvpExport = {
  label: 'RSVP Status',
  document_key: 'selected_status',
};
const csvEventSelectedStatusHeaderForRsvpExport = {
  label: 'RSVP Status',
  document_key: 'waitlist',
};
const eventRsvpStatusReadableLabel = {
  false: 'Confirmed',
  true: 'Waitlisted',
};
const exclusiveEventRsvpStatusReadableLabel = {
  [EVENT_SUBMISSION_SELECTED_STATUS.selected]: 'Selected',
  [EVENT_SUBMISSION_SELECTED_STATUS.notSelected]: 'Not Selected',
};
const cancelledRsvpLabel = 'Cancelled';

const CSV_FILE_TYPE = 'text/csv;charset=utf-8;';

function triggerCsvFileDownload(csvAsString: string, fileName: string): void {
  const byteOrderMark = '\ufeff';
  const blob = new Blob([byteOrderMark, csvAsString], {
    type: CSV_FILE_TYPE,
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadCsvData(
  csvData: unknown[] | UnparseObject<unknown>,
  filename: string,
  config?: UnparseConfig,
): void {
  const sanitizedFileName = sanitizeFileNameForCsv(filename);
  const csvAsString = unparse(csvData, config);
  triggerCsvFileDownload(csvAsString, sanitizedFileName);
}

export function sanitizeFileNameForCsv(fileName: string) {
  return fileName.toLowerCase().replace(/ /g, '-');
}

export function buildCsvDataForRsvpExport(
  opportunity: Opportunity,
  opportunitySubmission: OpportunitySubmission,
) {
  const groupId = get(opportunity, 'owner_group._id');
  const userMemberships = get(opportunitySubmission, 'accountId.groups', []);
  const membership = getMembership(userMemberships, groupId);
  const userDocument = {
    ...opportunitySubmission.accountId,
    groups: {
      ...membership,
    },
  };
  const csvRowData = [
    ...getHeaderKeyFromDocument(
      csvUserDocumentHeadersForRsvpExport,
      userDocument,
    ),
  ];
  const isActive = isEqual('active', opportunitySubmission.status);
  if (get(opportunity, 'submission_instructions[0]', '')) {
    csvRowData.push(get(opportunitySubmission, 'submissions.0', ''));
  }
  if (isEqual(OPPORTUNITY_TYPES.event, opportunity.opportunity_type)) {
    const isWaitlisted =
      get(
        opportunitySubmission,
        csvEventSelectedStatusHeaderForRsvpExport.document_key,
        false,
      ) || false;
    const readableLabel = !isActive
      ? cancelledRsvpLabel
      : get(eventRsvpStatusReadableLabel, `${isWaitlisted}`);
    csvRowData.push(readableLabel);
  } else {
    const selectedStatus = get(
      opportunitySubmission,
      csvExclusiveEventSelectedStatusHeaderForRsvpExport.document_key,
    );
    const readableLabel = !isActive
      ? cancelledRsvpLabel
      : get(exclusiveEventRsvpStatusReadableLabel, selectedStatus);
    csvRowData.push(readableLabel);
  }
  return csvRowData;
}

export function buildCsvHeaderForRsvpExport(opportunity: Opportunity) {
  const csvHeaderData = [
    ...getHeaderKey(csvUserDocumentHeadersForRsvpExport, 'label'),
  ];
  if (get(opportunity, 'submission_instructions[0]', '')) {
    csvHeaderData.push(get(opportunity, 'submission_instructions.0', ''));
  }
  if (isEqual(OPPORTUNITY_TYPES.event, opportunity.opportunity_type)) {
    csvHeaderData.push(csvEventSelectedStatusHeaderForRsvpExport.label);
  } else {
    csvHeaderData.push(
      csvExclusiveEventSelectedStatusHeaderForRsvpExport.label,
    );
  }
  return csvHeaderData;
}

export function getHeaderKey(
  headers: { label: string; document_key: string }[],
  key: string,
) {
  return headers.map((header: { label: string; document_key: string }) =>
    get(header, key, ''),
  );
}

export function getHeaderKeyFromDocument(
  headers: { label: string; document_key: string }[],
  document: any,
) {
  return headers.map((header: { label: string; document_key: string }) =>
    get(document, header.document_key, ''),
  );
}

export function buildCsvForAccountErrorsExport(accounts: UploadCSVAccount[]) {
  const csvData = accounts.map((account: UploadCSVAccount) => [
    account.email,
    account.errors?.join('\n') || '',
  ]);
  return [['email', 'errors'], ...csvData];
}
